import { useContext } from "react";

import { IButtonAction } from "config";
import { useGetButtonCommand } from "containers/DynamicForm/useButtonCommand";
import { TShowInsuranceApplicationCommand } from "containers/DynamicForm/useButtonCommand/useShowInsuranceApplication";
import { useNavigateToUri } from "modules/hooks/useNavigateToUri";
import { isFormExpired } from "utils/form";

import { DynamicFormContext } from "./DynamicFormContext";

export const BREAK_ACTIONS = "BREAK_ACTIONS";
export const EXPECTED_ACTION_ERROR = "EXPECTED_ACTION_ERROR";

/**
 * Handles generating ButtonFormItem actions
 * @param {Object} actions - IButtonAction, determines what method to generate by type and command
 * @param {boolean} [isNavigationButton=false] - Indicates if the action was triggered by a navigation button.
 * @return {Function} - executes each generated action method synchronously*
 *  @return {String} - return value of BREAK_ACTIONS breaks actions loop, used for certain action APIs
 */

export const useButtonAction = (actions: IButtonAction[], isNavigationButton: boolean = false) => {
  const navigateToUri = useNavigateToUri();
  const { getApiButtonCommand, getLinkButtonCommand } = useGetButtonCommand();
  const { dynamicForm } = useContext(DynamicFormContext);

  const createActionCommand = (buttonAction: IButtonAction) => {
    if (buttonAction.type === "api") {
      const apiAction = getApiButtonCommand(buttonAction.command);

      return (buttonOptions: IButtonActionOptions) =>
        apiAction(buttonAction.command.arguments || [], buttonOptions);
    } else if (buttonAction.type === "link") {
      const linkActionURL = getLinkButtonCommand(buttonAction.command);
      const linkNavigationAction = () => navigateToUri(linkActionURL);

      return linkNavigationAction;
    }
  };

  const handleButtonActions = async (options: Partial<IButtonActionOptions> = {}) => {
    // If expired, prefer expirationActions if present, else fallback to actions
    const actionsToRun =
      !isNavigationButton &&
      isFormExpired(dynamicForm.expiration) &&
      dynamicForm.expiration?.actions
        ? dynamicForm.expiration.actions
        : actions;

    const buttonActionInput: IButtonActionOptions = {
      ...options,
      actions: actionsToRun,
      formAction: undefined,
    };

    for (const buttonAction of actionsToRun) {
      const actionCommand = createActionCommand(buttonAction);

      const response = actionCommand && (await actionCommand(buttonActionInput));

      if (isActionResponseFormAction(response)) {
        buttonActionInput.formAction = response?.formAction;
      }

      if (response === BREAK_ACTIONS) {
        break;
      }
    }
  };

  return handleButtonActions;
};

/* ------------------    TYPES, PREDICATES    ------------------ */
export type IButtonActionOptions = TShowInsuranceApplicationCommand & TFormAction;
type TFormAction = { actions?: IButtonAction[]; formAction?: string; formScope?: string };
const isActionResponseFormAction = (response: unknown): response is TFormAction => {
  return (
    typeof response === "object" &&
    response !== null &&
    "formAction" in response &&
    (response as TFormAction).formAction !== undefined
  );
};
